import {Injectable} from "@angular/core";
import {isValidNumber, isVoid, snapToStrikeStep} from "../utils";
import {MessageBusService} from "../message-bus.service";
import {AtmStrikeService} from '../common-services/atm-strike-service/atm-strike.service';
import {AtmStrikeChangedEvent} from "../common-services/atm-strike-service/atm-strike-changed-event";
import {ApplicationSettingsService, AppSettingsUpdatedMessageTopic} from "../app-settings/application-settings.service";
import {LastQuoteCacheService} from "../last-quote-cache.service";
import {QuoteDto} from "../shell-communication/dtos/quote-dto.class";
import {ExpectedMoveRangeProcessor} from "./expected-move-range.processor";
import {isRangeModelResolvable} from "./expected-move-range-model";
import {OptionsChainService} from "../option-chains.service";
import {GetOptionChainShellResponse} from "../shell-communication/shell-dto-protocol";

export interface ExpectedMoveArgs {
    source: ExpectedMoveWhereToShow;
    column: ExpectedMoveColumnsToShow;
    strike: number;
    ticker: string;
    expiration: string;
    expirationTicker: string;
    underlyingTicker: string;
}

export type ExpectedMoveRangeModel = 'ATM Straddle' | 'Custom Percentage';

export type ExpectedMoveDaysToExpiration = '0' | '1' | '0 & 1' | 'All';

export type ExpectedMoveWhereToShow = 'OPG' | 'Hedge Matrix' | 'OPG & Hedge Matrix';

export type ExpectedMoveColumnsToShow = 'Expiration' | 'PnL' | 'Expiration & PnL';

export interface ExpectedMoveSettings {
    enabled?: boolean;
    whereToShow: ExpectedMoveWhereToShow;
    daysToExpiration: ExpectedMoveDaysToExpiration;
    rangeModel: ExpectedMoveRangeModel;
    rangeModelCustomPercent?: number;
    range1Enabled?: boolean;
    range2Enabled?: boolean;
    range2CustomPercent?: number;
    showAtmStraddleValue?: boolean;
    hmColumnsToShow: ExpectedMoveColumnsToShow;
}

export type ExpectedMoveRegion = 'near' | 'far';

export interface ExpectedMoveDisplay {
    values: number[];
    atmStraddleValue?: number;
    loading: boolean;
    invalid: boolean;
}

@Injectable({providedIn: 'root'})
export class ExpectedMoveService {

    constructor(
        private readonly _messageBusService: MessageBusService,
        private readonly _atmStrikeService: AtmStrikeService,
        private readonly _appSettings: ApplicationSettingsService,
        private readonly _lastQuoteCacheService: LastQuoteCacheService,
        private readonly _optionChainService: OptionsChainService,
    ) {
    }

    private _rangeProcessors = new Map<string, ExpectedMoveRangeProcessor>();
    private _chains = new Map<string, GetOptionChainShellResponse>();
    private _watchedTickers = new Set<string>();

    init() {
        this._messageBusService.of<AtmStrikeChangedEvent>('AtmStrikeChangedEvent')
            .subscribe(data => {
                this.onAtmChanged(data.payload);
            });

        this._messageBusService.of<QuoteDto[]>('QuoteDto')
            .subscribe(x => this.onQuote(x.payload));

        this._messageBusService.of(AppSettingsUpdatedMessageTopic)
            .subscribe(() => this.onSettingsChanged());

        this.watch('SPX');
        this.watch('SPY');

    }

    isStrikeInRange(args: ExpectedMoveArgs): boolean {
        return !!this.getStrikeRegion(args);
    }

    getStrikeRegion(args: ExpectedMoveArgs): ExpectedMoveRegion {
        if (!isValidNumber(args.strike) || !this.isDisplayAllowed(args)) { return undefined; }
        const bands = this.getDisplayProcessor(args).getDisplayBands();
        const index = bands.findIndex(b => args.strike >= b.rangeStart && args.strike <= b.rangeEnd);
        return index < 0 ? undefined : index === 0 ? 'near' : 'far';
    }

    private isDisplayAllowed(args: ExpectedMoveArgs): boolean {
        if (!args.expirationTicker || !args.underlyingTicker || !args.expiration) { return false; }
        const settings = this._appSettings.expectedMoveSettings;

        if (!settings?.enabled) {
            return false;
        }

        const whereToShow = this._appSettings.expectedMoveSettings.whereToShow || '';

        if (whereToShow.indexOf(args.source) < 0) {
            return false;
        }

        this.watch(args.underlyingTicker);

        const daysToExpiration = this._appSettings.expectedMoveSettings.daysToExpiration;

        if (daysToExpiration !== 'All') {
            if (!this._chains.has(args.underlyingTicker)) {
                return false;
            }

            const chain = this._chains.get(args.underlyingTicker);

            const zeroExpiration = chain.expirations[0]?.optionExpirationDate;
            const oneExpiration = chain.expirations[1]?.optionExpirationDate;

            if (daysToExpiration === '0 & 1') {
                if (zeroExpiration !== args.expiration && oneExpiration !== args.expiration) {
                    return false;
                }
            } else if (daysToExpiration === '0') {
                if (zeroExpiration !== args.expiration) {
                    return false;
                }
            } else if (daysToExpiration === '1') {
                if (oneExpiration !== args.expiration) {
                    return false;
                }
             }
        }

        if (whereToShow.indexOf('Hedge Matrix') !== -1) {
            if (args.source === 'Hedge Matrix') {
                const columnsToShow = this._appSettings.expectedMoveSettings?.hmColumnsToShow || 'PnL';

                if (columnsToShow.indexOf(args.column) === -1) {
                    return false;
                }
            }
        }

        return true;
    }

    private getDisplayProcessor(args: ExpectedMoveArgs): ExpectedMoveRangeProcessor {
        const id = `${args.underlyingTicker}:${args.expiration}`;

        let rangeProcessor = this._rangeProcessors.get(id);

        if (isVoid(rangeProcessor)) {
            rangeProcessor = this.createProcessor(args.expirationTicker, args.underlyingTicker, args.expiration);
        }

        return rangeProcessor;
    }

    getDisplay(args: ExpectedMoveArgs): ExpectedMoveDisplay {
        if (!this.isDisplayAllowed(args)) { return undefined; }
        const settings = this._appSettings.expectedMoveSettings;
        const processor = this.getDisplayProcessor(args);
        const custom = settings.rangeModel === 'Custom Percentage';
        const selected = !custom || settings.range1Enabled !== false || settings.range2Enabled === true;
        const resolvable = (!custom || settings.range1Enabled !== false) && isRangeModelResolvable(settings)
            || custom && settings.range2Enabled === true
            && isRangeModelResolvable({...settings, rangeModelCustomPercent: settings.range2CustomPercent});
        const values = processor.getDisplayBands().map(b => b.oneWayValue).filter(v => v > 0);
        return {
            values,
            atmStraddleValue: processor.atmStraddleValue,
            loading: selected && resolvable && !isValidNumber(processor.atmStraddleValue),
            invalid: selected && (!resolvable || isValidNumber(processor.atmStraddleValue) && !values.length)
        };
    }

    getDisplayText(args: ExpectedMoveArgs, label = 'Expected Range'): string {
        const display = this.getDisplay(args);
        if (!display) { return ''; }
        const lines: string[] = [];
        const values = Array.from(new Set(display.values.map(value => snapToStrikeStep(value, args.underlyingTicker))));
        if (values.length) { lines.push(label + ': ' + values.join(' | ')); }
        else if (display.invalid) { lines.push(label + ': N/A'); }
        else if (display.loading) { lines.push(label + ': ...'); }
        if (this._appSettings.expectedMoveSettings.showAtmStraddleValue) {
            const raw = display.atmStraddleValue;
            lines.push('(ATM Straddle: ' + (isValidNumber(raw) ? '$' + raw.toFixed(2) : '...') + ')');
        }
        return lines.join('\n');
    }

    hasExtendedDisplay(): boolean {
        const settings = this._appSettings.expectedMoveSettings;
        return !!settings?.showAtmStraddleValue || settings?.rangeModel === 'Custom Percentage'
            && (settings.range2Enabled === true || settings.range1Enabled === false);
    }

    isEnabledForOPG(): boolean {
        const settings = this._appSettings.expectedMoveSettings;
        if (!settings?.enabled) {
            return false;
        }
        const whereToShow = settings.whereToShow || '';
        return whereToShow.indexOf('OPG') >= 0;
    }

    /**
     * CR-20260727-001. Can the OPG Co-Pilot offer its "Expected Range" price-range forms?
     *
     * DELIBERATELY NOT isEnabledForOPG(). Owner ruling D2: the Co-Pilot gates on the ENABLE
     * CHECKBOX ONLY and ignores "Where To Show". Accepted consequence — with Where To Show set to
     * 'Hedge Matrix' the OPG header prints no expected range while the Co-Pilot still offers the
     * mode. That asymmetry is the ruling, not an oversight, so do NOT "fix" it by swapping in
     * isEnabledForOPG(); the two predicates answer different questions and must stay separate.
     *
     * Also false when the model does not resolve to a band — Custom Percentage with no usable
     * percent (owner ruling D3), or a blank rangeModel. Offering the mode there would send a
     * selection the backend must reject, so it is withheld instead. W14 moved that rule into the
     * shared isRangeModelResolvable() so the Co-Pilot gate, the processor and the OPG header cannot
     * drift apart; see that function for why both states are reachable. The delegation is
     * behaviour-preserving — D2 and D3 are unchanged.
     */
    isExpectedRangeUsableForCoPilot(): boolean {
        const settings = this._appSettings.expectedMoveSettings;
        if (!settings?.enabled) {
            return false;
        }

        return isRangeModelResolvable(settings);
    }

    /**
     * CR-20260727-001 W14. Do the current Expected Move settings resolve to a band at all?
     *
     * Answers ONLY the primary model question, independently of display switches. Retained for
     * existing primary consumers. The overlays now use getDisplay(), which distinguishes invalid
     * selected percentages (N/A) from missing quotes (...) and can show a valid Range 2 on its own.
     */
    hasResolvableRangeModel(): boolean {
        return isRangeModelResolvable(this._appSettings.expectedMoveSettings);
    }

    /**
     * CR-20260727-001. The model selection the Co-Pilot couriers to the skill, or null when the
     * mode is not usable (the caller must not be offering Expected Range at all in that case).
     *
     * COURIER, NOT A VALUE. This returns the user's MODEL, never a computed boundary or band —
     * resolving the expected range frontend-side is prohibited (owner ruling CR-20260715-001,
     * restated in ADR-035's rejected-alternatives list). The skill still does the math.
     */
    getCoPilotRangeModel(): { model: ExpectedMoveRangeModel, customPercent: number | null } | null {
        if (!this.isExpectedRangeUsableForCoPilot()) {
            return null;
        }

        const settings = this._appSettings.expectedMoveSettings;

        return {
            model: settings.rangeModel,
            // The panel stores a MULTIPLIER of the straddle (0.01-5.0 displayed as 1%-500% by the
            // box's "0#%" format), not a percent of the underlying. It crosses unconverted.
            customPercent: settings.rangeModel === 'Custom Percentage'
                ? settings.rangeModelCustomPercent
                : null
        };
    }

    getExpectedRangeValue(expirationTicker: string, underlyingTicker: string, expiration: string): number {
        const settings = this._appSettings.expectedMoveSettings;

        if (!settings.enabled) {
            return undefined;
        }

        const whereToShow = settings.whereToShow || '';
        if (whereToShow.indexOf('OPG') < 0) {
            return undefined;
        }

        if (!expirationTicker || !underlyingTicker || !expiration) {
            return undefined;
        }

        const id = underlyingTicker + ':' + expiration;
        let processor = this._rangeProcessors.get(id);

        if (!processor) {
            processor = this.createProcessor(expirationTicker, underlyingTicker, expiration);
        }

        return processor.oneWayValue;
    }

    private createProcessor(expirationTicker: string, underlyingTicker: string, expiration: string): ExpectedMoveRangeProcessor {
        const processor = new ExpectedMoveRangeProcessor(
            expirationTicker,
            underlyingTicker,
            expiration,
            this._appSettings.expectedMoveSettings,
            this._lastQuoteCacheService,
            this._messageBusService
        );
        const currentAtm = this._atmStrikeService.getCurrentAtm(underlyingTicker);
        processor.onAtmChanged(currentAtm);
        this._rangeProcessors.set(processor.id, processor);
        return processor;
    }

    private watch(ticker: string) {
        if (this._watchedTickers.has(ticker)) { return; }
        this._watchedTickers.add(ticker);
        this._atmStrikeService.watch(ticker);
        this._optionChainService.getChain(ticker).then((data) => {
            this._chains.set(ticker, data);
            this._messageBusService.publishAsync({topic: 'ExpectedMoveRangeChanged', payload: {ticker}});
        });
    }

    private onAtmChanged(payload: AtmStrikeChangedEvent) {
        this._rangeProcessors.forEach(rp => {
            if (rp.ticker === payload.underlying) {
                rp.onAtmChanged(payload.currentAtm);
            }
        });
    }

    private onQuote(payload: QuoteDto[]) {
        this._rangeProcessors.forEach(rp => {
            const quoteDto = payload.find(q => rp.matchesTicker(q.ticker));
            if (quoteDto) {
                rp.onQuote(quoteDto);
            }
        });
    }

    private onSettingsChanged() {
        const expectedMoveSettings = this._appSettings.expectedMoveSettings;
        this._rangeProcessors
            .forEach(rp => rp.onSettingsChanged(expectedMoveSettings))
    }
}
